import React, { useState, Component } from 'react';
import { supabase } from '../../lib/supabase';
import { FileJson, Store, CheckCircle2, AlertCircle, Loader2, Database, Layers, UtensilsCrossed, Settings2, ChefHat, Link2, MessageCircle, ArrowRight } from 'lucide-react';
// ─── Types ───────────────────────────────────────────────────
interface RestaurantPayload {
  name: string;
  slug: string;
  whatsapp_number?: string;
  [key: string]: any;
}
interface ParsedPayload {
  restaurant: RestaurantPayload;
  categories: any[];
  items: any[];
  options: any[];
  stations: any[];
}
interface CreateResult {
  success: boolean;
  restaurant_id?: number;
  restaurant_slug?: string;
  menu_import?: {
    categories_created_or_updated: number;
    items_created_or_updated: number;
    options_inserted: number;
  };
  stations_created?: number;
  station_mappings_created?: number;
  [key: string]: any;
}
interface ValidationState {
  status: 'idle' | 'valid' | 'error';
  errors: string[];
  counts: {
    categories: number;
    items: number;
    options: number;
    stations: number;
  };
}
// ─── Summary Card ────────────────────────────────────────────
function SummaryMetric({
  icon: Icon,
  label,
  value
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
}) {
  return <div className="bg-[#0B0B0B] border border-white/5 rounded-xl p-4 text-center" data-id="element-3456" data-mp="1zhw7tobl2v8m">
      <Icon className="w-4 h-4 text-white/30 mx-auto mb-2" data-id="element-3457" data-mp="1zk0hyo02sqhc" />
      <div className="text-2xl font-bold text-white" data-id="element-3458" data-mp="24gqzzgvfxw5e">{value}</div>
      <div className="text-[10px] text-white/40 uppercase tracking-widest mt-1" data-id="element-3459" data-mp="29fu3uxzopwbs">
        {label}
      </div>
    </div>;
}
// ─── Page Component ──────────────────────────────────────────
export function CreateRestaurantPage() {
  const [jsonInput, setJsonInput] = useState('');
  const [parsedData, setParsedData] = useState<ParsedPayload | null>(null);
  const [validation, setValidation] = useState<ValidationState>({
    status: 'idle',
    errors: [],
    counts: {
      categories: 0,
      items: 0,
      options: 0,
      stations: 0
    }
  });
  const [restaurantFields, setRestaurantFields] = useState({
    name: '',
    slug: '',
    whatsapp_number: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createResult, setCreateResult] = useState<CreateResult | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  // ─── Validate ────────────────────────────────────────────
  const handleValidate = () => {
    setParsedData(null);
    setCreateResult(null);
    setCreateError(null);
    const errors: string[] = [];
    if (!jsonInput.trim()) {
      setValidation({
        status: 'error',
        errors: ['El JSON está vacío.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    let data: any;
    try {
      data = JSON.parse(jsonInput);
    } catch {
      setValidation({
        status: 'error',
        errors: ['El JSON no es válido. Revisa la sintaxis.'],
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    // Structural validation
    if (!data.restaurant || typeof data.restaurant !== 'object' || Array.isArray(data.restaurant)) {
      errors.push('"restaurant" debe ser un objeto.');
    }
    if (!Array.isArray(data.categories)) {
      errors.push('"categories" debe ser un array.');
    }
    if (!Array.isArray(data.items)) {
      errors.push('"items" debe ser un array.');
    }
    if (!Array.isArray(data.options)) {
      errors.push('"options" debe ser un array.');
    }
    if (!Array.isArray(data.stations)) {
      errors.push('"stations" debe ser un array.');
    }
    if (errors.length > 0) {
      setValidation({
        status: 'error',
        errors,
        counts: {
          categories: 0,
          items: 0,
          options: 0,
          stations: 0
        }
      });
      return;
    }
    const counts = {
      categories: data.categories.length,
      items: data.items.length,
      options: data.options.length,
      stations: data.stations.length
    };
    setParsedData(data as ParsedPayload);
    setRestaurantFields({
      name: data.restaurant.name || '',
      slug: data.restaurant.slug || '',
      whatsapp_number: data.restaurant.whatsapp_number || ''
    });
    setValidation({
      status: 'valid',
      errors: [],
      counts
    });
  };
  // ─── Create ──────────────────────────────────────────────
  const handleCreate = async () => {
    if (!parsedData) return;
    // Re-validate
    if (validation.status !== 'valid') {
      handleValidate();
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    setCreateResult(null);
    try {
      const payloadToSend: ParsedPayload = {
        ...parsedData,
        restaurant: {
          ...parsedData.restaurant,
          name: restaurantFields.name,
          slug: restaurantFields.slug,
          whatsapp_number: restaurantFields.whatsapp_number
        }
      };
      const {
        data,
        error
      } = await supabase.rpc('create_restaurant_from_menu_json', {
        p_payload: payloadToSend
      });
      if (error) throw error;
      setCreateResult(data as CreateResult);
    } catch (err: any) {
      console.error('[CreateRestaurant] RPC error:', err);
      setCreateError(err.message || 'Error desconocido al crear el restaurante.');
    } finally {
      setIsCreating(false);
    }
  };
  // ─── Render ──────────────────────────────────────────────
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans selection:bg-[#006847] selection:text-white" data-id="element-3460" data-mp="2y6j7xb13zj64">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 space-y-8" data-id="element-3461" data-mp="338i8kd5gs1zd">
        {/* Header */}
        <header className="flex items-start gap-4" data-id="element-3462" data-mp="dk99q59q9egy">
          <div className="w-14 h-14 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center shrink-0" data-id="element-3463" data-mp="ifx0gp3uyfok">
            <Database className="w-7 h-7 text-[#006847]" data-id="element-3464" data-mp="3owdpzhucfgcf" />
          </div>
          <div data-id="element-3465" data-mp="3jyc5qtl6lazd">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-id="element-3466" data-mp="3jym0euh686be">
              Crear restaurante desde menú
            </h1>
            <p className="text-white/40 text-sm mt-1 max-w-xl" data-id="element-3467" data-mp="3txbfkfhoncny">
              Pega el JSON generado por el parser para crear restaurante, menú y
              KDS automáticamente.
            </p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-3468" data-mp="319fb2olvgggm">
          {/* ─── Left: JSON Input ─── */}
          <div className="lg:col-span-7 space-y-6" data-id="element-3469" data-mp="3fyliv0yrzf04">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/20" data-id="element-3470" data-mp="3fzd71qq1magd">
              <div className="flex items-center justify-between mb-4" data-id="element-3471" data-mp="2nh93vs0p5jtb">
                <h2 className="text-base font-semibold flex items-center gap-2" data-id="element-3472" data-mp="2nf4skp0gxij0">
                  <FileJson className="w-5 h-5 text-white/40" data-id="element-3473" data-mp="2ihms7n0iobmm" />
                  Payload JSON
                </h2>
                <button onClick={handleValidate} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium transition-all active:scale-95" data-id="element-3474" data-mp="xy0vj2vrxzgm">
                  Validar JSON
                </button>
              </div>

              <textarea value={jsonInput} onChange={e => setJsonInput(e.target.value)} placeholder="Pega aquí el JSON generado por el sistema..." className="w-full h-[420px] bg-[#0B0B0B] border border-white/10 rounded-xl p-4 font-mono text-[13px] leading-relaxed text-white/80 placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 resize-y transition-colors" spellCheck={false} data-id="element-3475" data-mp="syo7iet62mcg" />

              {/* Validation Error */}
              {validation.status === 'error' && <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-3476" data-mp="rzocsqg0370j">
                  <div className="flex items-center gap-2 text-red-400 mb-2" data-id="element-3477" data-mp="n13ckr0r0nc3">
                    <AlertCircle className="w-4 h-4" data-id="element-3478" data-mp="n36cq3vm7wuj" />
                    <strong className="text-sm font-semibold" data-id="element-3479" data-mp="j01ck7z0c792">
                      Errores de validación
                    </strong>
                  </div>
                  <ul className="space-y-1 text-sm text-red-300/80 ml-6 list-disc" data-id="element-3480" data-mp="1aq65iv0u22lw">
                    {validation.errors.map((err, i) => <li key={i} data-id="element-3481" data-mp="16q3zyarz8p1c" data-mpi={i}>{err}</li>)}
                  </ul>
                </div>}

              {/* Validation Success */}
              {validation.status === 'valid' && <div className="mt-4 p-4 bg-[#006847]/10 border border-[#006847]/20 rounded-xl" data-id="element-3482" data-mp="wyrb7s1h6ezj">
                  <div className="flex items-center gap-2 text-[#00a870] mb-4" data-id="element-3483" data-mp="1ufpzizc9h69v">
                    <CheckCircle2 className="w-4 h-4" data-id="element-3484" data-mp="1pmwwcfwfy5zw" />
                    <strong className="text-sm font-semibold" data-id="element-3485" data-mp="1wz344r48x9ib">
                      JSON válido — listo para crear
                    </strong>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-3486" data-mp="2200l50dirxi8">
                    <SummaryMetric icon={Layers} label="Categorías" value={validation.counts.categories} data-id="element-3487" data-mp="26w7vr6gv1x6i" />
                    <SummaryMetric icon={UtensilsCrossed} label="Items" value={validation.counts.items} data-id="element-3488" data-mp="2fww87wqlvpha" />
                    <SummaryMetric icon={Settings2} label="Opciones" value={validation.counts.options} data-id="element-3489" data-mp="2pojvdlg4qkcy" />
                    <SummaryMetric icon={ChefHat} label="Estaciones" value={validation.counts.stations} data-id="element-3490" data-mp="2yoyjeskhadk5" />
                  </div>
                </div>}
            </div>
          </div>

          {/* ─── Right: Preview & Actions ─── */}
          <div className="lg:col-span-5 space-y-6" data-id="element-3491" data-mp="teitkk1loepu">
            {/* Restaurant Preview */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl shadow-black/20 sticky top-6" data-id="element-3492" data-mp="ybreuti1oyvb">
              <h2 className="text-base font-semibold flex items-center gap-2 mb-6" data-id="element-3493" data-mp="136592skz0zye">
                <Store className="w-5 h-5 text-white/40" data-id="element-3494" data-mp="175e6q8j4thz8" />
                Datos del Restaurante
              </h2>

              <div className="space-y-4" data-id="element-3495" data-mp="1c6jz258vr388">
                <div data-id="element-3496" data-mp="1h3k72lpaig1z">
                  <label className="block text-[11px] font-medium text-white/40 mb-1.5 uppercase tracking-widest" data-id="element-3497" data-mp="1h4d4gw2czt7g">
                    Nombre
                  </label>
                  <div className="relative" data-id="element-3498" data-mp="3ivqbbqnw0b65">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3499" data-mp="3ix1afd6e0uqx" />
                    <input type="text" value={restaurantFields.name} onChange={e => setRestaurantFields({
                    ...restaurantFields,
                    name: e.target.value
                  })} disabled={!parsedData || isCreating} placeholder="Ej. Mi Restaurante" className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3500" data-mp="3nwdwhvpfpxsg" />
                  </div>
                </div>

                <div data-id="element-3501" data-mp="13i6auzq9ycml">
                  <label className="block text-[11px] font-medium text-white/40 mb-1.5 uppercase tracking-widest" data-id="element-3502" data-mp="13ipj1oq4hmnf">
                    Slug (URL)
                  </label>
                  <div className="relative" data-id="element-3503" data-mp="1008t1hk7d98d">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3504" data-mp="101bf9c6kynhy" />
                    <input type="text" value={restaurantFields.slug} onChange={e => setRestaurantFields({
                    ...restaurantFields,
                    slug: e.target.value
                  })} disabled={!parsedData || isCreating} placeholder="mi-restaurante" className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3505" data-mp="14y1yg8dolz7l" />
                  </div>
                </div>

                <div data-id="element-3506" data-mp="397w6gy0zrq4">
                  <label className="block text-[11px] font-medium text-white/40 mb-1.5 uppercase tracking-widest" data-id="element-3507" data-mp="3bu2yed3e37a">
                    WhatsApp
                  </label>
                  <div className="relative" data-id="element-3508" data-mp="3aw227lkl45rz">
                    <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-3509" data-mp="3pri1c500urv8" />
                    <input type="text" value={restaurantFields.whatsapp_number} onChange={e => setRestaurantFields({
                    ...restaurantFields,
                    whatsapp_number: e.target.value
                  })} disabled={!parsedData || isCreating} placeholder="50688888888" className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847]/60 focus:ring-1 focus:ring-[#006847]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3510" data-mp="3ks5dpi1btgrl" />
                  </div>
                </div>
              </div>

              {/* Create Button */}
              <div className="mt-8 pt-6 border-t border-white/10" data-id="element-3511" data-mp="2kjym1lolzl9s">
                <button onClick={handleCreate} disabled={!parsedData || isCreating || validation.status !== 'valid'} className="w-full flex items-center justify-center gap-2.5 bg-[#006847] hover:bg-[#005a3d] active:scale-[0.98] disabled:bg-white/5 disabled:text-white/25 text-white font-bold py-3.5 px-4 rounded-xl transition-all" data-id="element-3512" data-mp="2kml2kath9b45">
                  {isCreating ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-3513" data-mp="25t9a2h3bq24v" />
                      Creando restaurante...
                    </> : <>
                      <Database className="w-5 h-5" data-id="element-3514" data-mp="3h8xylcw8797s" />
                      Crear Restaurante
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3515" data-mp="15mm9759w2033" />
                    </>}
                </button>
              </div>

              {/* Error State */}
              {createError && <div className="mt-5 p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-3516" data-mp="wszk1y06rv2v">
                  <div className="flex items-start gap-3 text-red-400" data-id="element-3517" data-mp="rtwnz326oz71">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-3518" data-mp="mxpe4z6o33jt" />
                    <div className="text-sm" data-id="element-3519" data-mp="hxbea4f7yzwv">
                      <strong className="block font-semibold mb-1" data-id="element-3520" data-mp="hwi6r18fs6do">
                        Error al crear
                      </strong>
                      <p className="text-red-300/80" data-id="element-3521" data-mp="8xen9w5cxoyn">{createError}</p>
                    </div>
                  </div>
                </div>}

              {/* Success State */}
              {createResult && !createError && <div className="mt-5 p-5 bg-[#006847]/10 border border-[#006847]/30 rounded-xl" data-id="element-3522" data-mp="23ch4z7o602r0">
                  <div className="flex items-center gap-3 mb-5" data-id="element-3523" data-mp="2mezlugldoci3">
                    <div className="w-10 h-10 bg-[#006847]/20 rounded-full flex items-center justify-center shrink-0" data-id="element-3524" data-mp="2r714ck952zgk">
                      <CheckCircle2 className="w-5 h-5 text-[#00a870]" data-id="element-3525" data-mp="2dg5pmrbylo52" />
                    </div>
                    <div data-id="element-3526" data-mp="2if8v2cvupwdk">
                      <strong className="block text-sm font-bold text-white" data-id="element-3527" data-mp="2iefnj9p2j2ud">
                        ¡Restaurante creado!
                      </strong>
                      <span className="text-xs text-[#00a870]" data-id="element-3528" data-mp="3a5vflj9e9hrz">
                        Todos los datos fueron insertados correctamente.
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm bg-[#0B0B0B] p-4 rounded-xl border border-white/5" data-id="element-3529" data-mp="313maj8wwaglz">
                    <ResultRow label="ID Restaurante" value={createResult.restaurant_id} data-id="element-3530" data-mp="1kpnpnz6j82vv" />
                    <ResultRow label="Slug" value={createResult.restaurant_slug || restaurantFields.slug} data-id="element-3531" data-mp="1i5zege5dg8ih" />
                    <div className="border-t border-white/5 my-2" data-id="element-3532" data-mp="1r77byu02d5bi" />
                    <ResultRow label="Categorías creadas" value={createResult.menu_import?.categories_created_or_updated} data-id="element-3533" data-mp="1r4be15o7ugn2" />
                    <ResultRow label="Items creados" value={createResult.menu_import?.items_created_or_updated} data-id="element-3534" data-mp="uh7to7u42sfq" />
                    <ResultRow label="Opciones insertadas" value={createResult.menu_import?.options_inserted} data-id="element-3535" data-mp="13hd28lb1jwvh" />
                    <div className="border-t border-white/5 my-2" data-id="element-3536" data-mp="fpfsczr5jex0" />
                    <ResultRow label="Estaciones creadas" value={createResult.stations_created} data-id="element-3537" data-mp="arnvrsfkb4mh" />
                    <ResultRow label="Mapeos de estación" value={createResult.station_mappings_created} data-id="element-3538" data-mp="3qd1y69388d7q" />
                  </div>

                  {createResult.restaurant_slug && <div className="mt-4 text-center" data-id="element-3539" data-mp="47ol565wr0pt">
                      <a href={`/r/${createResult.restaurant_slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#00a870] hover:text-white transition-colors font-medium" data-id="element-3540" data-mp="95zrh6wzut3a">
                        Ver menú público
                        <ArrowRight className="w-4 h-4" data-id="element-3541" data-mp="rw7u7nws7hiy" />
                      </a>
                    </div>}
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// ─── Result Row Helper ───────────────────────────────────────
function ResultRow({
  label,
  value
}: {
  label: string;
  value: any;
}) {
  return <div className="flex justify-between items-center" data-id="element-3542" data-mp="2cgcx4rhtatn5">
      <span className="text-white/50" data-id="element-3543" data-mp="27foxxijfj6hu">{label}</span>
      <strong className="text-white font-mono text-xs" data-id="element-3544" data-mp="22fu87ibe4myd">{value ?? '—'}</strong>
    </div>;
}